import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../Button";
import { TextField } from "../form-fields/TextField";
import { APIs } from "../../services/Apis";
import { toast } from "react-toastify";
import {
  ClipboardDocumentIcon,
  CheckIcon,
  SparklesIcon,
  ArrowLeftIcon,
  TableCellsIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";

const DEFAULT_PORTS = {
  mysql: "3306",
  postgresql: "5432",
  sqlserver: "1433",
  mongodb: "27017",
};

const DATA_SOURCES = [
  {
    id: "mysql",
    name: "MySQL / PlanetScale",
    subtitle: "Connect via credentials",
    icon: "/assets/database_icons/mysql.png",
    active: true,
    dbType: "mysql",
  },
  {
    id: "postgresql",
    name: "PostgreSQL",
    subtitle: "",
    icon: "/assets/database_icons/postgres.png",
    active: false,
  },
  {
    id: "supabase",
    name: "Supabase",
    subtitle: "",
    icon: "/assets/database_icons/supabase.png",
    active: false,
  },
  {
    id: "mongodb",
    name: "MongoDB Atlas",
    subtitle: "",
    icon: "/assets/database_icons/mongo.png",
    active: false,
  },
  {
    id: "firebase",
    name: "Firebase",
    subtitle: "",
    icon: "/assets/database_icons/firebase.png",
    active: false,
  },
  {
    id: "snowflake",
    name: "Snowflake",
    subtitle: "",
    icon: "/assets/database_icons/snowflake.png",
    active: false,
  },
  {
    id: "google-sheets",
    name: "Google Sheets",
    subtitle: "",
    icon: null,
    heroIcon: TableCellsIcon,
    heroIconClass: "text-green-600",
    active: false,
  },
  {
    id: "wordpress",
    name: "WordPress",
    subtitle: "",
    icon: null,
    heroIcon: GlobeAltIcon,
    heroIconClass: "text-blue-600",
    active: false,
  },
];

const IT_EMAIL_TEXT = `Hi,

I'm setting up a connection between our database and Fluvo (https://fluvo.ai), a tool we're using for automated reporting and workflows.

To complete the setup, I need the following database connection details:

1. Database Type (e.g. MySQL, PostgreSQL, SQL Server, MongoDB)
2. Database Host / URL
3. Port Number
4. Database Name
5. Username (with read access)
6. Password

Additionally, Fluvo requires inbound access from their server. Please whitelist the following IP address:

  13.238.150.55

Could you please provide the above details and confirm the IP has been whitelisted?

Thank you!`;

export default function DatabaseConnectionTab({
  initialData = {},
  isSample = false,
  hasExistingDatabase = false,
  onSampleGenerated,
}) {
  const [selectedSource, setSelectedSource] = useState(null);
  const [isGeneratingSample, setIsGeneratingSample] = useState(false);
  const [formData, setFormData] = useState({
    dbUsername: "",
    dbPassword: "",
    dbUrl: "",
    dbType: "mysql",
    dbName: "",
    dbPort: "3306",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [savedSuccessfully, setSavedSuccessfully] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      const hasExistingPassword =
        (initialData.dbPassword && initialData.dbPassword !== "") ||
        (initialData.databasePassword && initialData.databasePassword !== "");

      setFormData({
        dbUsername:
          initialData.dbUsername || initialData.databaseUsername || "",
        dbPassword: hasExistingPassword ? "xxxxx" : "",
        dbUrl: initialData.dbUrl || initialData.databaseUrl || "",
        dbType: initialData.dbType || initialData.databaseType || "mysql",
        dbName: initialData.dbName || initialData.databaseName || "",
        dbPort: initialData.dbPort || initialData.databasePort || "3306",
      });
    }
  }, [initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectSource = (source) => {
    if (!source.active) return;
    setSelectedSource(source);
    const port = DEFAULT_PORTS[source.dbType] || "3306";
    setFormData((prev) => ({
      ...prev,
      dbType: source.dbType || "mysql",
      dbPort: prev.dbPort || port,
    }));
  };

  const handleBackToPicker = () => {
    setSelectedSource(null);
  };

  const handleGenerateSample = async () => {
    setIsGeneratingSample(true);
    try {
      const response = await APIs.generateSampleDatabase();
      if (response.success) {
        toast.success("Sample database created successfully!");
        if (onSampleGenerated) onSampleGenerated();
      } else {
        toast.error(response.message || "Failed to generate sample database");
      }
    } catch (err) {
      console.error("Failed to generate sample database:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsGeneratingSample(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (formData.dbPassword === "xxxxx") {
      toast.error(
        "Please enter your database password. The password field cannot be left as 'xxxxx'.",
      );
      return;
    }

    if (!formData.dbPassword || formData.dbPassword.trim() === "") {
      toast.error("Please enter your database password.");
      return;
    }

    setIsSaving(true);

    try {
      const response = await APIs.saveBusinessDatabase(
        formData.dbUsername,
        formData.dbPassword,
        formData.dbUrl,
        formData.dbType,
        formData.dbName,
        formData.dbPort,
      );

      if (response.success) {
        toast.success("Database connection details saved successfully");
        setFormData((prev) => ({ ...prev, dbPassword: "xxxxx" }));
        setSavedSuccessfully(true);
        setTimeout(() => navigate("/dashboard"), 2000);
      } else {
        toast.error(
          response.message || "Failed to save database connection details",
        );
      }
    } catch (err) {
      console.error("Failed to save database connection details:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(IT_EMAIL_TEXT);
      setEmailCopied(true);
      toast.success("Email copied to clipboard");
      setTimeout(() => setEmailCopied(false), 2000);
    } catch {
      toast.error("Failed to copy email");
    }
  };

  const isConnected =
    savedSuccessfully || !!(initialData?.dbName || initialData?.databaseName);

  if (hasExistingDatabase || selectedSource) {
    return (
      <CredentialForm
        selectedSource={selectedSource}
        hasExistingDatabase={hasExistingDatabase}
        isConnected={isConnected}
        formData={formData}
        isSaving={isSaving}
        emailCopied={emailCopied}
        onInputChange={handleInputChange}
        onSave={handleSave}
        onCopyEmail={handleCopyEmail}
        onBack={handleBackToPicker}
      />
    );
  }

  return (
    <DataSourcePicker
      isSample={isSample}
      isGeneratingSample={isGeneratingSample}
      onSelectSource={handleSelectSource}
      onGenerateSample={handleGenerateSample}
    />
  );
}

function DataSourcePicker({
  isSample,
  isGeneratingSample,
  onSelectSource,
  onGenerateSample,
}) {
  return (
    <div>
      <div className="py-5 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">Connect your data</h2>
        <p className="mt-1 text-sm text-gray-500">
          Choose a data source to get started. This is necessary for Fluvo to
          generate reports and automations for you.
        </p>
      </div>

      {isSample && (
        <div className="mt-5 flex items-start gap-3 rounded-lg border border-purple-200/60 bg-purple-50/50 p-4">
          <svg
            className="size-5 shrink-0 text-purple-500 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
            />
          </svg>
          <div>
            <p className="text-sm font-medium text-purple-900">
              You're currently using sample data
            </p>
            <p className="mt-0.5 text-sm text-purple-700">
              Connect a real data source below to go live with your automations.
            </p>
          </div>
        </div>
      )}

      <div className="mt-6">
        <DataSourceCard
          icon={<SparklesIcon className="size-5 text-amber-500" />}
          name="Sample Data"
          subtitle="AI-generated realistic data based on your business"
          action={
            isSample ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                <CheckIcon className="size-3.5" />
                Done
              </span>
            ) : (
              <button
                type="button"
                onClick={onGenerateSample}
                disabled={isGeneratingSample}
                className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 transition-colors cursor-pointer disabled:opacity-50"
              >
                {isGeneratingSample ? (
                  <>
                    <svg
                      className="animate-spin size-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="3"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="size-4" />
                    Generate
                  </>
                )}
              </button>
            )
          }
        />
      </div>

      <div className="relative mt-6">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-white px-3 text-sm text-gray-500">or</span>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3">
        {DATA_SOURCES.map((source) => {
          const IconComponent = source.heroIcon;
          const disabled = !source.active;

          return (
            <div key={source.id}>
              <DataSourceCard
                icon={
                  source.icon ? (
                    <img
                      src={source.icon}
                      alt={source.name}
                      className="size-6 object-contain"
                    />
                  ) : IconComponent ? (
                    <IconComponent
                      className={`size-5 ${source.heroIconClass || "text-gray-500"}`}
                    />
                  ) : null
                }
                name={source.name}
                subtitle={source.subtitle}
                disabled={disabled}
                action={
                  disabled ? (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">
                      Coming Soon
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onSelectSource(source)}
                      className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 transition-colors cursor-pointer"
                    >
                      Connect
                    </button>
                  )
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DataSourceCard({ icon, name, subtitle, action, disabled = false }) {
  return (
    <div
      className={`flex items-center gap-4 rounded-xl border px-4 py-4 transition-colors ${
        disabled
          ? "border-gray-100 bg-gray-50/50 opacity-60"
          : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-medium ${disabled ? "text-gray-400" : "text-gray-900"}`}
        >
          {name}
        </p>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  );
}

function CredentialForm({
  selectedSource,
  hasExistingDatabase,
  isConnected,
  formData,
  isSaving,
  emailCopied,
  onInputChange,
  onSave,
  onCopyEmail,
  onBack,
}) {
  const sourceName = selectedSource?.name || "Database";

  return (
    <div>
      <div className="py-5 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center gap-3">
          {!hasExistingDatabase && (
            <button
              type="button"
              onClick={onBack}
              className="flex size-8 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <ArrowLeftIcon className="size-4 text-gray-500" />
            </button>
          )}
          <div>
            <h2 className="text-lg font-medium text-gray-900">
              {hasExistingDatabase
                ? "Database Connection"
                : `Connect ${sourceName}`}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {hasExistingDatabase
                ? "Manage your database connection settings"
                : "Enter your database credentials below"}
            </p>
          </div>
        </div>
        <div>
          {isConnected ? (
            <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
              Connected
            </span>
          ) : (
            <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10">
              Not Connected
            </span>
          )}
        </div>
      </div>
      <div className="py-5">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-blue-900">
              Need help? Send this to your IT team
            </h3>
            <button
              type="button"
              onClick={onCopyEmail}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-100 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-200 transition-colors cursor-pointer"
            >
              {emailCopied ? (
                <>
                  <CheckIcon className="h-4 w-4" />
                  Copied!
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Copy Email
                </>
              )}
            </button>
          </div>
          <pre className="text-sm text-blue-800 whitespace-pre-wrap font-sans bg-blue-100/60 rounded-md p-3 leading-relaxed">
            {IT_EMAIL_TEXT}
          </pre>
        </div>
      </div>
      <form onSubmit={onSave} className="py-5 space-y-6">
        <TextField
          label="Database URL (Host) *"
          type="text"
          id="dbUrl"
          name="dbUrl"
          value={formData.dbUrl}
          onChange={onInputChange}
          placeholder="host.example.com"
          required
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <TextField
            label="Port *"
            type="text"
            id="dbPort"
            name="dbPort"
            value={formData.dbPort}
            onChange={onInputChange}
            placeholder=""
            required
          />

          <TextField
            label="Database Name *"
            type="text"
            id="dbName"
            name="dbName"
            value={formData.dbName}
            onChange={onInputChange}
            placeholder="my_database"
            required
          />
        </div>

        <TextField
          label="Username *"
          type="text"
          id="dbUsername"
          name="dbUsername"
          value={formData.dbUsername}
          onChange={onInputChange}
          placeholder="user"
          required
        />

        <TextField
          label="Password *"
          type="password"
          id="dbPassword"
          name="dbPassword"
          value={formData.dbPassword}
          onChange={onInputChange}
          placeholder={
            formData.dbPassword === "xxxxx"
              ? "Enter your database password"
              : "Enter database password"
          }
          required
        >
          {formData.dbPassword === "xxxxx" && (
            <p className="mt-1 text-sm text-gray-500">
              Please enter your actual database password to save changes.
            </p>
          )}
        </TextField>

        <div className="flex justify-end pt-4 border-t border-gray-200">
          <Button
            type="submit"
            variant="solid"
            color="primary"
            loading={isSaving}
            disabled={isSaving}
          >
            Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
}
